import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from '@/features/auth/hooks';
import { LoadingScreen } from '@/components/shared/loading-screen';
import { ErrorState } from '@/components/shared/error-state';

/**
 * Route guard that requires an authenticated session.
 *
 * - While the auth check is in flight, shows a loading screen.
 * - On network error (server unreachable), shows an error state instead of
 *   logging the user out – we don't treat outages as confirmed sign-outs.
 * - On 401, redirects to `/login` with a `returnTo` search param that
 *   preserves the intended destination.
 * - Validates `returnTo` paths to prevent open redirect attacks.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status, refetch } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return <LoadingScreen />;
  }

  if (status === 'error') {
    return (
      <ErrorState
        title="Connection Error"
        message="We couldn't verify your session. The server might be temporarily unavailable."
        onRetry={() => { void refetch(); }}
      />
    );
  }

  if (status === 'unauthenticated') {
    const returnTo = location.pathname + location.search;
    const safeReturn = isValidReturnPath(returnTo) ? returnTo : '/dashboard';
    return <Navigate to={`/login?returnTo=${encodeURIComponent(safeReturn)}`} replace />;
  }

  return <>{children}</>;
}

/**
 * Validates that a return path is a safe, relative path on this origin.
 * Prevents open-redirect attacks via crafted `returnTo` values.
 */
function isValidReturnPath(path: string): boolean {
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//')) return false;          // protocol-relative URL
  if (path.includes('\\')) return false;             // backslash tricks
  try {
    const url = new URL(path, window.location.origin);
    return url.origin === window.location.origin;
  } catch {
    return false;
  }
}
